import { create } from 'zustand'
import type {
  SessionState,
  SessionStateData,
  DeepPartial,
  CameraDevice,
  PrinterDevice,
  LogEntry
} from '@renderer/types/sessionState'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from './configState'
import { resolveLogLevel } from './activityLogLevel'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { Filter, FrameOption } from '@renderer/types/configState'
import { fetchLUTData, fetchOverlayImage } from '@renderer/utils/lutUtils'
import { useSettingsStore } from './settingsStore'
import { useAuthStore } from './authStore'
import { isSimulationActive } from '@renderer/lib/simulation'
import { useHubStore } from './hubStore'
import { getHomePath } from '@renderer/utils/navigationUtils'
import { fetchProjectCatalogRaw, type RawProjectCatalog } from '@renderer/utils/projectCatalogApi'
import { cacheProjectCatalog, readCachedProject } from '@renderer/utils/projectConfigCache'
import { rewriteLegacyCdnUrls } from '@renderer/utils/legacyCdnHost'
import {
  clearCheckpoint,
  readCheckpoint,
  RESUME_PAID_SESSION_FLAG,
  syncCheckpoint
} from '@renderer/utils/sessionCheckpoint'
import { useFeatureFlagsStore } from './featureFlagsStore'

type PreloadApi = {
  saveActivityLogs?: (
    sessionId: string,
    logs: Array<{
      message: string
      timestamp: Date
      level: 'info' | 'warn' | 'error'
      page?: string
      details?: Record<string, unknown>
    }>
  ) => Promise<void>
  saveSession?: (
    sessionData: Record<string, unknown>
  ) => Promise<{ success: boolean; error?: string }>
  getInternetStatus?: () => Promise<boolean>
  triggerSync?: () => Promise<{ success: boolean }>
}

const getPreloadApi = (): PreloadApi | undefined => {
  if (typeof window === 'undefined') return undefined
  return (window as typeof window & { api?: PreloadApi }).api
}

/**
 * Check real network connectivity status
 * Uses window.api.getInternetStatus() if available (Electron), otherwise falls back to navigator.onLine
 */
async function checkNetworkStatus(): Promise<boolean> {
  if (typeof window === 'undefined') return true // Assume online in SSR
  const api = getPreloadApi()
  if (api?.getInternetStatus) {
    return await api.getInternetStatus()
  }
  return navigator.onLine // Fallback to browser API
}

/**
 * Waits for the settings store to be hydrated from localStorage.
 * Zustand's persist middleware loads asynchronously, so we need to wait
 * for it to complete before reading persisted values like selectedPrinter.
 *
 * @param maxWaitMs Maximum time to wait in milliseconds (default: 5000ms)
 * @returns Promise that resolves when settings are hydrated
 */
async function waitForSettingsHydration(maxWaitMs = 5000): Promise<void> {
  const startTime = Date.now()

  return new Promise((resolve) => {
    const checkSettings = (): void => {
      const settings = useSettingsStore.getState()

      // deviceId is always set (either from localStorage or generated),
      // so if it exists, the store has been initialized
      // For hydration check, we look for a persisted value or the generated default
      // If deviceId exists and it's not the default empty string, we're hydrated
      const isHydrated = settings.deviceId && settings.deviceId.length > 0

      if (isHydrated) {
        resolve()
        return
      }

      // Check timeout
      if (Date.now() - startTime > maxWaitMs) {
        console.warn('[SETTINGS] Timeout waiting for settings hydration. Proceeding anyway.', {
          deviceId: settings.deviceId
        })
        resolve() // Resolve anyway to not block initialization
        return
      }

      // Check again after a short delay
      setTimeout(checkSettings, 50)
    }

    // Start checking immediately
    checkSettings()
  })
}

interface User {
  _id: string
  email: string
  username: string
}

interface InitializeParams {
  projectId: string
  user: User
  eventId?: string
}

/**
 * Loads filters, frames, and stickers for a project into the config store (no session state changes).
 *
 * Network first. If the fetch fails and the offline cache (utils/projectConfigCache)
 * holds this project's catalogue, replay that through the same setters — a hub
 * booth re-runs this on every hub → member → hub hop, and a Wi-Fi drop between
 * guests must not kill the session (plan §4.4). Online, a successful fetch
 * refreshes the cache.
 */
export async function loadProjectCatalogFromApi(projectId: string): Promise<{
  configuredFilters: Filter[]
  configuredFrames: FrameOption[]
  photoRatio: number | null
}> {
  let raw: RawProjectCatalog
  try {
    raw = await fetchProjectCatalogRaw(projectId)
    cacheProjectCatalog(projectId, raw)
  } catch (error) {
    const cached = readCachedProject(projectId)?.catalog
    if (!cached) throw error
    console.warn(
      `[INIT] offline_catalog_used: catalogue fetch failed for ${projectId}, using cached copy`,
      error
    )
    raw = cached
  }
  // Frames, stickers and filters stored before the CDN move still point at workers.dev,
  // which some venue networks block. Rewritten here so every page and the print read the
  // reachable host, whether the catalogue came from the network or the offline cache.
  raw = rewriteLegacyCdnUrls(raw)

  const configuredFilters: Filter[] = raw.filters
  const frameData = raw.frameData
  const configuredStickers = raw.stickers

  const lutFilterIds = configuredFilters
    .filter((f) => f.filterMode === 'lut' && f.lutData?.size)
    .map((f) => f._id)
  if (lutFilterIds.length > 0) {
    await Promise.all(lutFilterIds.map((id) => fetchLUTData(id)))
  }

  // Warm every overlay image here, alongside the LUTs and for the same reason: the draw
  // paths read the cache synchronously and skip a miss, so an overlay that has not
  // arrived by capture time is simply absent from the photo. Deduped by URL, and a
  // failure resolves to null rather than rejecting — a booth whose overlay CDN is
  // unreachable still runs its session.
  const overlayUrls = Array.from(
    new Set(
      configuredFilters
        .map((f) => f.overlay?.url)
        .filter((url): url is string => Boolean(url))
    )
  )
  if (overlayUrls.length > 0) {
    await Promise.all(overlayUrls.map((url) => fetchOverlayImage(url)))
  }

  const configuredFrames: FrameOption[] = []

  if (frameData.categoryModeEnabled) {
    const categories = frameData.frameCategories ?? []
    categories.forEach((category: { frames: FrameOption[] }) => {
      category.frames.forEach((frame) => {
        if (!configuredFrames.some((f) => f._id === frame._id)) {
          configuredFrames.push(frame)
        }
      })
    })
    useConfigStore.getState().setFrameCategories(frameData.frameCategories ?? [])
  } else {
    configuredFrames.push(...(frameData.frames ?? []))
  }

  await useConfigStore.getState().setFilters(configuredFilters)
  useConfigStore.getState().setFrames(configuredFrames)

  const editSettings = useConfigStore.getState().getPageSettings('edit')
  const stickerIds = editSettings?.stickerIds || []
  const filteredStickers = Array.isArray(configuredStickers)
    ? configuredStickers.filter((sticker) => stickerIds.includes(sticker._id))
    : []
  useConfigStore.getState().setStickers(filteredStickers)

  const initialFrame: FrameOption | undefined = configuredFrames[0]
  const photoImage = initialFrame?.drawParams?.images?.find((img) =>
    img.type.startsWith('photo-')
  )
  const photoRatio = photoImage ? photoImage.width / photoImage.height : null

  return { configuredFilters, configuredFrames, photoRatio }
}

// Update SessionState type to include new methods
interface ExtendedSessionState extends SessionStateData {
  sessionUploadError: string | null
  setSessionUploadError: (error: string | null) => void
  initializeSession: (params: InitializeParams) => Promise<void>
  refreshProjectCatalog: (projectId: string) => Promise<void>
  revokeGalleryUrls: () => Promise<void>
  uploadSessionState: (targetSessionId?: string, stateSnapshot?: DeepPartial<SessionStateData> | null) => Promise<void>
  endSession: () => void
  cleanupSession: (reason?: string) => Promise<void>
  setSessionState: (update: DeepPartial<SessionStateData>) => void
  /**
   * Record one activity-log line for the current session.
   *
   * Pass `details.level = 'warn' | 'error'` to file it as a failure (`'warning'`
   * is read as `'warn'`); anything else lands as `info`. See `resolveLogLevel`.
   */
  logActivity: (
    message: string,
    details?: Record<string, unknown>,
    pageFromClick?: string,
    sessionId?: string
  ) => void
}

// Session-scoped log storage
interface SessionLogs {
  [sessionId: string]: LogEntry[]
}

const sessionLogs: SessionLogs = {}

// Queue for managing session updates
class SessionUpdateQueue {
  private queue: Array<() => Promise<void>> = []
  private isProcessing = false

  async add(updateFn: () => Promise<void>): Promise<void> {
    this.queue.push(updateFn)
    if (!this.isProcessing) {
      await this.process()
    }
  }

  getIsProcessing(): boolean {
    return this.isProcessing
  }

  private async process(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) return

    this.isProcessing = true
    while (this.queue.length > 0) {
      const updateFn = this.queue.shift()
      if (updateFn) {
        try {
          await updateFn()
        } catch (error) {
          console.error('Error processing session update:', error)
          // Keep failed update in queue for retry
          this.queue.unshift(updateFn)
          break
        }
      }
    }
    this.isProcessing = false
  }
}

const sessionUpdateQueue = new SessionUpdateQueue()

// Per-session debounce timers, keyed by sessionId. Previously a single shared timer was
// cleared+rebound on every logActivity call regardless of session, so a newly-started
// session cancelled the previous (dying) session's pending upload — silently dropping its
// final ~10s of logs and erasing WHY it was reset. Keyed timers + an explicit
// flushSessionLogs() drain on reset fix that.
const logSyncTimeouts: Record<string, NodeJS.Timeout> = {}

/**
 * Saves activity logs to SQLite storage via IPC
 * @param sessionId - Current session ID
 * @param logsMap - Map of session logs by session ID
 */
async function saveActivityLogsToStorage(sessionId: string, logsMap: SessionLogs): Promise<void> {
  const api = getPreloadApi()
  if (!api?.saveActivityLogs) {
    return
  }

  const allSessionLogs = [...(logsMap[sessionId] || [])]
  if (allSessionLogs.length === 0) {
    return
  }

  // Convert to format expected by API
  const logsToSave = allSessionLogs.map((log) => ({
    message: log.message,
    timestamp: log.timestamp,
    level: (log.level || 'info') as 'info' | 'warn' | 'error',
    page: log.page,
    details: log.details || {}
  }))

  // @ts-ignore - saveActivityLogs exists on window.api at runtime
  await api.saveActivityLogs(sessionId, logsToSave)

  // Only drop what was actually persisted; entries logged during the await stay queued.
  const saved = new Set(allSessionLogs)
  sessionLogs[sessionId] = (sessionLogs[sessionId] || []).filter((entry) => !saved.has(entry))
}

/**
 * Immediately drain ONE session's buffered activity logs: cancel its pending debounce,
 * upload directly if online, otherwise persist to SQLite for later sync. Call this for the
 * just-ended session BEFORE cleanup so its final logs (including the reset reason) are never
 * lost — the debounce alone can be pre-empted by the next session starting within its window.
 */
export async function flushSessionLogs(sessionId: string): Promise<void> {
  if (!sessionId) return
  // Sim Mode is read-only: simulated sessions must never reach the
  // activity-logs API or the offline SQLite sync queue.
  if (isSimulationActive()) {
    delete sessionLogs[sessionId]
    return
  }

  const timer = logSyncTimeouts[sessionId]
  if (timer) {
    clearTimeout(timer)
    delete logSyncTimeouts[sessionId]
  }

  // Snapshot what this flush is responsible for. The live array keeps receiving
  // entries while the upload is in flight, and clearing it wholesale afterwards
  // silently dropped every one of them — visible as gaps in a session's story
  // (a capture's "photo captured" line missing while its neighbours are present).
  const logsToSend = [...(sessionLogs[sessionId] || [])]
  if (logsToSend.length === 0) return

  const isOnline = await checkNetworkStatus()
  if (isOnline) {
    const result = await uploadActivityLogsToApi(sessionId, logsToSend)
    if (result.success) {
      const sent = new Set(logsToSend)
      sessionLogs[sessionId] = (sessionLogs[sessionId] || []).filter((entry) => !sent.has(entry))
      return
    }
    console.warn('[ActivityLog] Direct flush failed, falling back to SQLite:', result.error)
  }

  // Offline or direct upload failed — persist to SQLite (syncManager uploads it later).
  if (getPreloadApi()?.saveActivityLogs) {
    await saveActivityLogsToStorage(sessionId, sessionLogs)
  }
}

/**
 * Upload activity logs directly to API
 */
async function uploadActivityLogsToApi(
  sessionId: string,
  logs: LogEntry[]
): Promise<{ success: boolean; error?: string }> {
  try {
    const authState = useAuthStore.getState()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const token = authState.user?.cookie || await (window as any).api?.getAuthToken?.()

    const payload = {
      sessionId,
      logs: logs.map((log) => ({
        message: log.message,
        timestamp: log.timestamp,
        level: log.level || 'info',
        page: log.page,
        details: log.details || {}
      }))
    }

    // Electron: bearer from auth store or IPC. Web: `user.cookie` is unset; NextAuth cookies are sent with credentials: 'include' (see resolveAuthSession on API).
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${process.env.VITE_API_URL || ''}/api/activity-logs`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const errorText = await response.text()
      return { success: false, error: `API error: ${response.status} ${errorText}` }
    }

    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

const getInitialState = (): SessionStateData => {
  return {
    id: '',
    projectId: '',
    userId: '',
    eventId: undefined,
    userEmail: undefined,
    mode: undefined,
    timeline: {
      appLaunchTime: new Date(),
      startTime: null,
      endTime: null,
      duration: null,
      isCompleted: false
    },
    userPreferences: {
      filterId: null,
      frameId: null,
      copies: 0,
      retakeCount: 0,
      retakeSessionsCompleted: 0,
      framePhotoRatio: null,
      useGIF: true,
      useRecording: true,
      useVideo: true,
      useDigital: true,
      usePrint: true,
      usePax: false,
      printGalleryQrOnStrip: true,
      selectedCamera: 'primary',
      kotakSingleCamera: false,
      email: '',
      phoneNumber: '',
      satisfactionRating: null,
      satisfactionTimestamp: null
    },
    userConsent: {
      photoPublication: false,
      consentTimestamp: new Date()
    },
    devices: {
      camera: null,
      printer: null
    },
    transaction: {
      orderId: null,
      amount: 0,
      discount: 0,
      qrUrl: null,
      expiryTime: null,
      serverTime: null,
      status: 'pending',
      extraPrintPaymentStatus: undefined,
      selectedPackages: [],
      printed: false,
      extraPrintAmount: 0,
      extraPrintCopies: 0
    },
    gallery: {
      id: '',
      stripUrl: null,
      capturedItems: [],
      filterCache: {},
      liveModeUrl: null,
      liveModeProcessing: false
    },
    galleryKey: '',
    logs: [] as LogEntry[]
  }
}

const logStateChange = (update: DeepPartial<SessionStateData>): void => {
  if (process.env.NODE_ENV === 'development') {
    const changes: string[] = []

    if (update.timeline) {
      if (update.timeline.startTime) changes.push('session started')
      if (update.timeline.endTime) changes.push('session ended')
      if (update.timeline.isCompleted !== undefined)
        changes.push(`session completion status changed to ${update.timeline.isCompleted}`)
    }

    if (update.userPreferences) {
      if (update.userPreferences.filterId !== undefined)
        changes.push(`filter changed to ${update.userPreferences.filterId}`)
      if (update.userPreferences.frameId !== undefined)
        changes.push(`frame changed to ${update.userPreferences.frameId}`)
      if (update.userPreferences.copies !== undefined)
        changes.push(`copies set to ${update.userPreferences.copies}`)
      if (update.userPreferences.retakeCount !== undefined)
        changes.push(`retake count set to ${update.userPreferences.retakeCount}`)
      if (update.userPreferences.useGIF !== undefined)
        changes.push(`GIF usage set to ${update.userPreferences.useGIF}`)
      if (update.userPreferences.useRecording !== undefined)
        changes.push(`recording usage set to ${update.userPreferences.useRecording}`)
      if (update.userPreferences.useVideo !== undefined)
        changes.push(`video usage set to ${update.userPreferences.useVideo}`)
      if (update.userPreferences.selectedCamera !== undefined)
        changes.push(`selected camera changed to ${update.userPreferences.selectedCamera}`)
    }

    if (update.devices) {
      if (update.devices.camera !== undefined)
        changes.push(`camera device updated to ${update.devices.camera?.name || 'null'}`)
      if (update.devices.printer !== undefined)
        changes.push(`printer device updated to ${update.devices.printer?.name || 'null'}`)
    }

    if (update.transaction) {
      if (update.transaction.status !== undefined)
        changes.push(`transaction status changed to ${update.transaction.status}`)
      if (update.transaction.amount !== undefined)
        changes.push(`transaction amount set to ${update.transaction.amount}`)
      if (update.transaction.orderId !== undefined)
        changes.push(`transaction order ID set to ${update.transaction.orderId}`)
    }

    if (update.gallery) {
      if (update.gallery.capturedItems && update.gallery.capturedItems.length > 0)
        changes.push(`captured ${update.gallery.capturedItems.length} items`)
      if (update.gallery.liveModeUrl !== undefined)
        changes.push(`live mode URL set to ${update.gallery.liveModeUrl}`)
      if (update.gallery.liveModeProcessing !== undefined)
        changes.push(`live mode processing status set to ${update.gallery.liveModeProcessing}`)
    }
    if (update.galleryKey !== undefined) changes.push(`galleryKey set to ${update.galleryKey}`)

    const logMessage = `Session state updated:\n${changes.map((change) => `- ${change}`).join('\n')}.`
    console.log(logMessage)
  }
}

export const useSessionStateStore = create<ExtendedSessionState>((set, get) => ({
  ...getInitialState(),
  sessionUploadError: null,
  setSessionUploadError: (error: string | null): void => set({ sessionUploadError: error }),
  initializeSession: async ({ projectId, user, eventId }: InitializeParams): Promise<void> => {
    // Canonical project id = the loaded config's _id (always a Mongo ObjectId).
    // In digital mode the route param (and thus the value some callers pass) is
    // a *slug*; the config is loaded by ProjectRoute before this runs, so its
    // _id is the resolved id. This guards every downstream consumer
    // (sessionState.projectId, catalog fetch, session/gallery upload) from ever
    // persisting a slug. In kiosk config._id === projectId, so this is a no-op.
    const configId = useConfigStore.getState().config?._id
    const resolvedProjectId =
      configId && /^[a-f0-9]{24}$/i.test(configId) ? configId : projectId
    if (get().projectId === resolvedProjectId) {
      return
    }
    useConfigStore.getState().resetLoadingProgress()
    try {
      // Wait for settings store to be hydrated before reading persisted values
      // This prevents race conditions where initializeSession runs before
      // localStorage data is loaded into the store
      await waitForSettingsHydration()

      const { configuredFilters, configuredFrames, photoRatio } =
        await loadProjectCatalogFromApi(resolvedProjectId)

      // Get current state for fallback logic
      const currentState = get()

      // Get default devices from settings store (now guaranteed to be hydrated)
      const { selectedCamera, selectedPrinter } = useSettingsStore.getState()

      // Fallback logic: if device is missing from settings, try to preserve from previous session
      // This handles cases where settings might have been cleared but we want to maintain
      // device continuity across sessions
      const cameraToUse = selectedCamera || currentState.devices?.camera?.id
      const printerToUse = selectedPrinter || currentState.devices?.printer?.id

      // Log warnings if devices are missing (for debugging)
      if (!selectedPrinter && currentState.devices?.printer) {
        console.warn(
          '[INIT] Printer not found in settings store, using previous session printer:',
          {
            previousPrinter: currentState.devices.printer.name,
            settingsStoreState: {
              selectedPrinter: useSettingsStore.getState().selectedPrinter,
              deviceId: useSettingsStore.getState().deviceId
            }
          }
        )
      } else if (!selectedPrinter && !currentState.devices?.printer) {
        console.warn('[INIT] No printer found in settings store or previous session')
      }

      if (!selectedCamera && currentState.devices?.camera) {
        console.warn('[INIT] Camera not found in settings store, using previous session camera:', {
          previousCamera: currentState.devices.camera.name
        })
      }

      // Initialize session state with all fetched data and default devices
      set((state) => ({
        ...state,
        projectId: resolvedProjectId,
        userId: user._id,
        eventId,
        userPreferences: {
          ...state.userPreferences,
          filterId: configuredFilters[0]?._id,
          frameId: configuredFrames[0]?._id,
          framePhotoRatio: photoRatio,
          selectedCamera: 'primary',
          satisfactionRating: null,
          satisfactionTimestamp: null
        },
        devices: {
          camera: cameraToUse
            ? ({
                id: cameraToUse,
                name: cameraToUse,
                status: 'ready',
                type: 'camera'
              } as CameraDevice)
            : null,
          printer: printerToUse
            ? ({
                id: printerToUse,
                name: printerToUse,
                status: 'ready'
              } as PrinterDevice)
            : null
        }
      }))
    } catch (error) {
      console.error('Error initializing session:', error)
      throw new Error(`${(error as Error).message || 'Failed to initialize session'}`)
    }
  },
  refreshProjectCatalog: async (projectId: string): Promise<void> => {
    useConfigStore.getState().resetLoadingProgress()
    await waitForSettingsHydration()
    await loadProjectCatalogFromApi(projectId)
  },
  logActivity: (
    message: string,
    details?: Record<string, unknown>,
    pageFromClick?: string,
    sessionId?: string
  ): void => {
    const currentSessionId = sessionId || get().id

    let page = pageFromClick
    if (!page) {
      const pathParts = window.location.href.split('/').filter(Boolean)
      page = pathParts.pop() || 'welcome'
    }
    console.log(
      `${message} [${details?.to ? `to ${details.to} page` : `in ${page} page`}]`,
      '\n',
      details || ''
    )

    const logEntry: LogEntry = {
      message,
      details,
      page: page,
      level: resolveLogLevel(details),
      timestamp: new Date(),
      ...(currentSessionId ? { sessionId: currentSessionId } : {})
    }

    set((state) => ({
      logs: [...state.logs, logEntry]
    }))

    if (!currentSessionId) {
      // Kept in the in-memory `logs` array above for the dev console, but it can
      // go no further: /api/activity-logs rejects an empty sessionId with a 400,
      // and there is no session document for it to hang off. Loud on purpose —
      // this drop is what made the karcis booths' print failures invisible.
      console.error(
        `[ActivityLog] Dropped "${message}" — no session id. Activity logs require a session; create one before logging.`
      )
      return
    }

    // Store in session-scoped storage
    if (!sessionLogs[currentSessionId]) {
      sessionLogs[currentSessionId] = []
    }
    sessionLogs[currentSessionId].push(logEntry)

    // Debounced batch upload, keyed PER SESSION so another session's logging never cancels
    // this session's pending flush (that race dropped a session's last logs before a reset).
    if (logSyncTimeouts[currentSessionId]) clearTimeout(logSyncTimeouts[currentSessionId])
    logSyncTimeouts[currentSessionId] = setTimeout(() => {
      flushSessionLogs(currentSessionId).catch((error) => {
        console.error('[ActivityLog] Debounced flush failed:', error)
      })
    }, 10000)
  },
  revokeGalleryUrls: async (): Promise<void> => {
    // Import backgroundVideoProcessor to check for active tasks
    const { backgroundVideoProcessor } = await import('@renderer/utils/backgroundVideoProcessor')

    // Check if there are any active video processing tasks that might need these blob URLs
    const hasActiveVideoTasks = backgroundVideoProcessor.hasActiveVideoTasks()

    if (hasActiveVideoTasks) {
      console.log('Delaying blob URL cleanup - video processing tasks are still active')
      // These three lines used to be pushed straight into `state.logs` with `set()`,
      // which never reaches `sessionLogs[sessionId]` and so never reaches the server:
      // dev-console only. They are the whole story of a lost recording — the timeout
      // below fires, the source blobs are revoked out from under a still-running
      // encode, and the guest's paid video never appears. Route them through
      // logActivity so a booth in the field can be diagnosed without a console.
      get().logActivity('Delaying blob URL cleanup - video processing tasks are still active', {
        hasActiveVideoTasks
      })

      // Wait for video processing to complete before cleaning up
      const waitStartedAt = Date.now()
      const waitForVideoCompletion = async (): Promise<void> => {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            const stillProcessing = backgroundVideoProcessor.hasActiveVideoTasks()

            if (!stillProcessing) {
              clearInterval(checkInterval)
              console.log('Video processing completed - proceeding with blob URL cleanup')
              get().logActivity('Video processing completed - proceeding with blob URL cleanup', {
                waitedMs: Date.now() - waitStartedAt
              })
              resolve()
            }
          }, 1000)

          // Timeout after 30 seconds to prevent indefinite waiting.
          //
          // This ceiling is the one that loses paid recordings. Measured over a
          // 7-day window, a framed strip video takes a median of ~86s to finish
          // (10s..206s) — well past 30s — and revoking here pulls the source photo
          // and clip blobs out from under the encode that is still reading them.
          // The same blobs feed the individual-photo uploads, which is why those
          // sessions also land with gaps in their photo set.
          //
          // Raising it is NOT a safe unilateral fix: `revokeGalleryUrls()` is
          // awaited before the booth navigates home (useSessionReset /
          // useSessionCleanup), so a longer wait parks the next guest on the
          // Result screen for that long. The fix is to stop blocking the reset on
          // this at all — tracked separately. Until then, at least say out loud
          // that it happened.
          setTimeout(() => {
            clearInterval(checkInterval)
            console.log(
              'Timeout waiting for video processing - proceeding with blob URL cleanup anyway'
            )
            get().logActivity(
              'Timeout waiting for video processing - revoking blobs while an encode is still running',
              { waitedMs: Date.now() - waitStartedAt, level: 'error' }
            )
            resolve()
          }, 30000)
        })
      }

      await waitForVideoCompletion()
    }

    // Get current state for cleanup
    const currentState = get()

    // Cleanup gallery URLs
    currentState.gallery.capturedItems?.forEach((item) => {
      if (item.photoUrl && item.photoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.photoUrl)
      }
      if (item.videoUrl && item.videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.videoUrl)
      }
    })

    console.log('Blob URL cleanup completed')
    set((state) => ({
      logs: [
        ...state.logs,
        {
          message: 'Blob URL cleanup completed',
          details: {},
          page: 'sessionState',
          level: 'info',
          timestamp: new Date()
        }
      ]
    }))
  },
  endSession: (): void => {
    set((state) => ({
      ...state,
      timeline: {
        ...state.timeline,
        endTime: new Date(),
        duration: state.timeline.startTime
          ? new Date().getTime() - state.timeline.startTime.getTime()
          : null
      }
    }))
  },
  uploadSessionState: async (
    targetSessionId?: string,
    stateSnapshot?: DeepPartial<SessionStateData> | null
  ): Promise<void> => {
    await sessionUpdateQueue.add(async () => {
      try {
        await stateUploader.uploadSessionState(
          targetSessionId,
          stateSnapshot as Record<string, unknown> | null | undefined
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (message.startsWith('NON_TRANSIENT:')) {
          set({ sessionUploadError: message })
        }
        throw err
      }
    })
  },
  cleanupSession: async (reason?: string): Promise<void> => {
    const currentState = get()
    const dyingSessionId = currentState.id

    // Record WHY the session ended and flush its logs synchronously BEFORE clearing state.
    // Without this a reset left no trace, and the next session's first log could pre-empt the
    // dying session's pending upload — erasing exactly the evidence of what reset it.
    if (dyingSessionId) {
      try {
        get().logActivity(
          `Session reset (${reason || 'unspecified'})`,
          { reason: reason || 'unspecified' },
          undefined,
          dyingSessionId
        )
        // Kick off the flush now, but don't let a slow/flaky upload stall the operator's
        // reset — the per-session buffer (sessionLogs[dyingSessionId]) keeps the logs so the
        // flush still completes in the background even if we stop awaiting it here.
        await Promise.race([
          flushSessionLogs(dyingSessionId),
          new Promise<void>((resolve) => setTimeout(resolve, 1500))
        ])
      } catch (e) {
        console.error('[SESSION] Failed to flush logs on cleanup:', e)
      }
    }

    // Emit a single booth session_ended event with a computed outcome before we
    // reset state. cleanupSession is the one chokepoint every session-end path
    // funnels through (useSessionCleanup + useSessionReset). The store guards
    // against double-emit per sessionId. Renderer + server reconcile are
    // complementary (dedup by sessionId) — see automation-plan 01.
    try {
      const sessionId = (currentState as { id?: string }).id
      if (sessionId) {
        const { useBoothEventStore } = await import('@renderer/stores/boothEventStore')
        const { usePrintStore } = await import('@renderer/stores/printStore')
        const completed = currentState.timeline?.isCompleted === true
        const captureFailed = currentState.gallery?.stripGenerationStatus === 'failed'
        const printErrored = usePrintStore.getState().status === 'error'
        const outcome = completed
          ? 'completed'
          : captureFailed
            ? 'capture_error'
            : printErrored
              ? 'print_error'
              : 'abandoned'
        useBoothEventStore.getState().endBoothSession(sessionId, outcome, {
          txStatus: currentState.transaction?.status,
          isCompleted: completed,
          stripGenerationStatus: currentState.gallery?.stripGenerationStatus,
          printed: currentState.transaction?.printed
        })
      }
    } catch (err) {
      console.warn('[BoothEvent] failed to emit session_ended:', err)
    }

    // Wait for critical operations with 2s timeout
    const criticalChecks: Promise<void>[] = [
      // Check session upload queue
      new Promise<void>((resolve) => {
        let checks = 0
        const checkInterval = setInterval(() => {
          checks++
          if (!sessionUpdateQueue.getIsProcessing() || checks >= 20) {  // 20 * 100ms = 2s
            clearInterval(checkInterval)
            resolve()
          }
        }, 100)
      }),
      // Check video processing (reuse existing pattern from revokeGalleryUrls)
      (async (): Promise<void> => {
        const { backgroundVideoProcessor } = await import('@renderer/utils/backgroundVideoProcessor')
        let checks = 0
        return new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            checks++
            if (!backgroundVideoProcessor.hasActiveVideoTasks() || checks >= 20) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 100)
        })
      })()
    ]
    
    await Promise.race([
      Promise.all(criticalChecks),
      new Promise<void>((resolve) => setTimeout(resolve, 2000))  // Max 2s wait
    ])
    
    const appLaunchTime = currentState.timeline.appLaunchTime
    const devices = currentState.devices
    const projectId = currentState.projectId

    // OWNERSHIP GUARD — the "paid guest wiped mid-payment" fix (Bahela Keraton).
    // cleanupSession is async (log flush race + upload/video drains above take up to
    // ~3.5s), and callers fire it more than once per session end: a double-tapped
    // "Beranda", or the result-timeout racing a tap. The FIRST invocation resets the
    // store and navigates home; Welcome then mounts and starts the NEXT customer's
    // session within ~100ms. Any TRAILING invocation used to reach this point seconds
    // later and unconditionally reset the store — wiping the NEW session's id and
    // transaction while its Welcome kept showing a live payment QR. With the store id
    // emptied, the payment status poll no-ops forever (no expiryTime), so the guest
    // pays a QR the booth can never confirm, and any navigation into a PageWrapper
    // route bounces `!sessionId` straight back to Welcome — "sudah bayar tapi balik
    // ke beranda". Only the invocation that still OWNS the store may reset it.
    if (get().id !== dyingSessionId) {
      console.warn(
        `[SESSION] Skipping cleanup reset for '${dyingSessionId || '(empty)'}' — store now owned by '${get().id || '(empty)'}' (trailing duplicate cleanup)`
      )
      return
    }

    // A deliberate end of this session: it must not be offered back after a reload.
    if (readCheckpoint()?.sessionId === dyingSessionId) clearCheckpoint()

    useConfigStore.getState().resetLoadingProgress()

    // Reset state but keep app launch time and project ID
    set({
      ...getInitialState(),
      projectId,
      devices,
      timeline: {
        ...getInitialState().timeline,
        appLaunchTime
      },
      userPreferences: {
        ...getInitialState().userPreferences,
        selectedCamera: 'primary',
        satisfactionRating: null,
        satisfactionTimestamp: null
      },
      logs: []
    })
  },
  setSessionState: (update: DeepPartial<SessionStateData>): void => {
    set((state) => {
      const newState = {
        ...state,
        ...(update as Partial<SessionState>),
        timeline: {
          ...state.timeline,
          ...(update.timeline || {})
        },
        userPreferences: {
          ...state.userPreferences,
          ...(update.userPreferences || {})
        },
        userConsent: {
          ...state.userConsent,
          ...(update.userConsent || {})
        },
        devices: {
          ...state.devices,
          ...(update.devices || {})
        },
        transaction: {
          ...state.transaction,
          ...(update.transaction || {})
        },
        gallery: {
          ...state.gallery,
          ...(update.gallery || {})
        },
        galleryKey:
          update.galleryKey !== undefined
            ? update.galleryKey
            : update.gallery?.id !== undefined
              ? update.gallery.id
              : state.galleryKey,
        logs: [...state.logs, ...(update.logs || [])]
      } as SessionState

      logStateChange(update)
      return newState
    })
  }
}))

// Keep a paid, unfinished session recoverable across the booth's own renderer
// recovery (see utils/sessionCheckpoint). Writes only when the kept fields change,
// and only while the resume_paid_session flag is on.
useSessionStateStore.subscribe((state) => {
  if (useFeatureFlagsStore.getState().flags[RESUME_PAID_SESSION_FLAG]) syncCheckpoint(state)
})

// Custom hooks for different session management scenarios.
// `reason` is a short machine-friendly tag (e.g. 'operator_exit', 'camera_recovery_timeout')
// recorded on the session's activity log before it is cleared, so resets are diagnosable.
export const useSessionReset = (): ((reason?: string) => Promise<void>) => {
  const navigate = useNavigate()
  // Navigate "home" by the raw route param, NOT the store's projectId. The store
  // holds the resolved Mongo id, which in digital mode differs from the URL slug
  // — using it here would rewrite `dreambooth.app/<slug>` to `.../<objectId>` and
  // strand the slug for the rest of the visit. In kiosk the route param already
  // IS the id, so this is unchanged there.
  const { projectId: routeParam } = useParams()
  const { projectId } = useSessionStateStore()
  const { uploadSessionState, revokeGalleryUrls, cleanupSession } = useSessionStateStore()

  return async (reason?: string) => {
    // Fire-and-forget upload
    uploadSessionState().catch(() => {})
    await revokeGalleryUrls()
    await cleanupSession(reason)
    // Hub members go home to the HUB Welcome (multi-project hub, plan D5).
    navigate(getHomePath(routeParam || projectId, useHubStore.getState()))
  }
}

export const useSessionCleanup = (): ((reason?: string) => Promise<void>) => {
  const navigate = useNavigate()
  // See useSessionReset: navigate by the URL's route param so the digital slug
  // survives; falling back to the resolved id only when the param is absent.
  const { projectId: routeParam } = useParams()
  const { projectId } = useSessionStateStore()
  const { revokeGalleryUrls, cleanupSession } = useSessionStateStore()

  return async (reason?: string) => {
    await revokeGalleryUrls()
    await cleanupSession(reason)
    navigate(getHomePath(routeParam || projectId, useHubStore.getState()))
  }
}
